import { AIFloatingButton } from "@/components/Floating";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Receipt, CheckCircle2, Award, ShoppingCart, CreditCard, Play, Sparkles, Lightbulb, ShieldCheck, ChevronRight, LogOut } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Btn, Card, Spinner, ErrorNote } from "@/components/PortalPage";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/lso";

const CodeEntry = ({ onRedeem, busy, primary }) => {
  const [code, setCode] = useState("");
  return (
    <div className="mt-4 w-full rounded-xl border border-[#333] bg-[#0A0A0A] p-4">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-neutral-400">{primary ? "Enter your 6-digit access code" : "Already paid? Enter your 6-digit code:"}</p>
      <input data-testid="rental-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000"
        className="w-full rounded-lg border border-[#333] bg-[#1A1A1A] py-3 text-center font-mono text-3xl font-extrabold tracking-[0.5em] text-white outline-none focus:border-[var(--accent)]" />
      <Btn className="mt-3" variant={primary ? "primary" : "outline"} busy={busy} disabled={code.length !== 6} onClick={() => onRedeem(code)} data-testid="rental-redeem-btn">{primary ? "REDEEM & UNLOCK COURSE" : "REDEEM CODE"}</Btn>
    </div>
  );
};

const Tool = ({ icon: Icon, title, body, to, testId }) => {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)} data-testid={testId} className="flex w-full items-center gap-4 rounded-2xl border border-[#333] bg-[#1A1A1A] p-4 text-left hover:border-[var(--accent)]">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]"><Icon size={24} /></span>
      <span className="flex-1"><span className="block font-bold">{title}</span><span className="text-xs text-neutral-400">{body}</span></span>
      <ChevronRight size={18} className="text-neutral-500" />
    </button>
  );
};

export default function LsoRental() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => api("/lso-rental/purchase/me").then(setInfo).catch(() => setInfo({ has_access: false, purchase: null, price_usd: 30, validity_days: 90 })), []);
  useEffect(() => { load(); }, [load]);

  const buy = async () => {
    setBusy("buy"); setErr(null);
    try {
      const r = await api("/lso-rental/purchase", { method: "POST", body: {} });
      if (r.fee_waived) toast.success(r.message || "Your course fee is waived.");
      else if (r.purchase?.qbo_invoice_link) toast.success(`${r.reused ? "Invoice already sent" : "Invoice created"} — emailed to ${user.email}.`);
      else toast("Request received — ALR will send your invoice shortly.");
      await load();
    } catch (e) { setErr(e.message || "Couldn't create invoice. Try again or contact support."); } finally { setBusy(null); }
  };
  const redeem = async (code) => {
    setBusy("redeem"); setErr(null);
    try {
      await api("/lso-rental/redeem-code", { method: "POST", body: { code } });
      toast.success("Access granted — you now have 90 days to complete the training.");
      await load();
    } catch (e) { setErr(e.message || "That code is invalid or for another account."); } finally { setBusy(null); }
  };

  const p = info?.purchase;
  const price = info?.price_usd ?? 30;
  const state = !info ? null : info.has_access ? "redeemed" : p?.access_code && p.status !== "redeemed" ? "paid" : ["invoice_sent", "pending"].includes(p?.status) ? "invoice_sent" : "no_purchase";

  return (
    <PortalPage eyebrow="CLASS 4 FIBER LASER CLEANING · ANSI Z136.1" title="LSO Operator Safety Training" back={null} testId="lso-rental-page"
      actions={<button onClick={logout} data-testid="logout-button" className="flex items-center gap-2 rounded-xl border border-[#333] bg-[#1A1A1A] px-3 py-2 text-xs font-bold"><LogOut size={16} /> Sign out</button>}>
      {!info && <Spinner />}
      <ErrorNote>{err}</ErrorNote>
      {state === "no_purchase" && (
        <Card className="flex flex-col items-center text-center" data-testid="rental-state-no-purchase">
          <Lock size={42} className="text-[var(--accent)]" />
          <p className="mt-3 font-display text-2xl font-bold uppercase">Course Access — ${price}</p>
          <p className="mt-2 text-sm text-neutral-400">The ALR Class 4 Laser Cleaning Operator Safety Training is required before operating rental laser equipment. Purchase access for ${price} — valid for {info.validity_days ?? 90} days after redeeming your code.</p>
          <ul className="my-4 w-full space-y-1.5 text-left text-sm">
            {["10 modules · 19 lessons · ANSI Z136.1 aligned", "20-question final exam · 80% to pass", "Operator Safety Training Certificate (90-day validity)", "QBO invoice emailed to you for fast payment"].map((b) => (
              <li key={b} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[var(--accent)]" />{b}</li>
            ))}
          </ul>
          <Btn busy={busy === "buy"} onClick={buy} data-testid="rental-buy-btn"><ShoppingCart size={16} /> BUY LSO COURSE · ${price}</Btn>
        </Card>
      )}
      {state === "invoice_sent" && (
        <Card className="flex flex-col items-center text-center" data-testid="rental-state-invoice-sent">
          <Receipt size={42} className="text-[#f5a623]" />
          <p className="mt-3 font-display text-2xl font-bold uppercase">{p.qbo_invoice_link ? "Invoice sent — awaiting payment" : "Request received"}</p>
          <p className="mt-2 text-sm text-neutral-400">
            {p.qbo_invoice_link
              ? `Your QuickBooks invoice for $${p.amount_usd} has been emailed to ${user.email}. After payment you'll receive a 6-digit access code by email.`
              : `ALR will email your $${p.amount_usd} invoice to ${user.email} shortly. After payment you'll receive a 6-digit access code by email.`}
          </p>
          {p.qbo_invoice_link && <Btn className="mt-4" onClick={() => window.open(p.qbo_invoice_link, "_blank")} data-testid="rental-pay-invoice"><CreditCard size={16} /> PAY INVOICE NOW</Btn>}
          {p.qbo_invoice_number && <p className="mt-2 text-xs text-neutral-500">QBO Invoice #{p.qbo_invoice_number}</p>}
          <CodeEntry onRedeem={redeem} busy={busy === "redeem"} />
        </Card>
      )}
      {state === "paid" && (
        <Card className="flex flex-col items-center text-center" style={{ borderColor: "#58CD36" }} data-testid="rental-state-paid">
          <CheckCircle2 size={42} className="text-[#58CD36]" />
          <p className="mt-3 font-display text-2xl font-bold uppercase">Payment received</p>
          <p className="mt-2 text-sm text-neutral-400">Your 6-digit access code was emailed to {user.email}. Enter it below to unlock the course.</p>
          <CodeEntry primary onRedeem={redeem} busy={busy === "redeem"} />
        </Card>
      )}
      {state === "redeemed" && (
        <>
          <Card className="flex flex-col items-center text-center" style={{ borderColor: "#58CD36" }} data-testid="rental-state-redeemed">
            <Award size={42} className="text-[#58CD36]" />
            <p className="mt-3 font-display text-2xl font-bold uppercase">Access granted</p>
            <p className="mt-2 text-sm text-neutral-400">Your access is valid until <b className="text-white">{fmtDate(info.access_expires_at)}</b>. Complete the 10-module training and pass the final exam to earn your certificate.</p>
            <Btn className="mt-4" onClick={() => navigate("/lso-course")} data-testid="rental-start-course"><Play size={16} /> START / RESUME COURSE</Btn>
          </Card>
          <Tool testId="rental-ai-tile" icon={Sparkles} title="ALR Cleaning Helper" body="Ask the AI about laser cleaning settings, safety, and PPE." to="/ai-assistant" />
          <Tool testId="rental-settings-tile" icon={Lightbulb} title="Laser Cleaning Settings" body="Browse the preset library filtered by substrate and contaminant." to="/cleaning-settings" />
          <Tool testId="rental-safety-tile" icon={ShieldCheck} title="Safety Resources" body="Class 4 laser safety references, SDS, PPE, and controlled-area protocols." to="/section/safety_resources" />
        </>
      )}
      {info && <button onClick={load} className="mx-auto block text-xs font-bold text-[var(--accent)]" data-testid="rental-refresh">Refresh status</button>}
      {state === "redeemed" && <AIFloatingButton />}
    </PortalPage>
  );
}
